import type { TFunction } from 'i18next';

/**
 * A sentence the server reports by code, with the values to fill into it. The server also sends
 * English `message` text for the CSV report and the API; the panel never shows that.
 */
export interface CodedMessage {
  code: MessageCode;
  params: Record<string, string | number>;
  message: string;
}

/**
 * Each message code the server sends, mapped to its translation. The keys are written out so the
 * typed `t` can check them. A code the server adds must be added here and in every locale.
 */
export const MESSAGE_KEYS = {
  key_format: 'admin.adFormats.messages.key_format',
  name_required: 'admin.adFormats.messages.name_required',
  status_invalid: 'admin.adFormats.messages.status_invalid',
  unknown_group: 'admin.adFormats.messages.unknown_group',
  unknown_spec_profile: 'admin.adFormats.messages.unknown_spec_profile',
  unknown_format: 'admin.adFormats.messages.unknown_format',
  unknown_profile: 'admin.adFormats.messages.unknown_profile',
  owner_type_invalid: 'admin.adFormats.messages.owner_type_invalid',
  width_invalid: 'admin.adFormats.messages.width_invalid',
  height_invalid: 'admin.adFormats.messages.height_invalid',
  pixels_invalid: 'admin.adFormats.messages.pixels_invalid',
  whole_number: 'admin.adFormats.messages.whole_number',
  property_twice: 'admin.adFormats.messages.property_twice',
  unknown_property: 'admin.adFormats.messages.unknown_property',
  provenance_invalid: 'admin.adFormats.messages.provenance_invalid',
  limited_twice: 'admin.adFormats.messages.limited_twice',
  unknown_text_field: 'admin.adFormats.messages.unknown_text_field',
  value_empty: 'admin.adFormats.messages.value_empty',
  format_group_locked: 'admin.adFormats.messages.format_group_locked',
  size_format: 'admin.adFormats.messages.size_format',
  number_format: 'admin.adFormats.messages.number_format',
  kb_format: 'admin.adFormats.messages.kb_format',
  range_format: 'admin.adFormats.messages.range_format',
  number_list_format: 'admin.adFormats.messages.number_list_format',
  boolean_format: 'admin.adFormats.messages.boolean_format',
  margins_format: 'admin.adFormats.messages.margins_format',
  file_skipped: 'admin.adFormats.messages.file_skipped',
  unknown_columns: 'admin.adFormats.messages.unknown_columns',
} as const;

export type MessageCode = keyof typeof MESSAGE_KEYS;

/**
 * The panel's text for a server message, in the current language. The key is still checked: it
 * must be one of the resource keys. The params are not, since typed `t` cannot take a union of
 * keys that carry different values.
 */
export function messageText(t: TFunction<'admin'>, message: CodedMessage): string {
  return String(t(MESSAGE_KEYS[message.code], message.params as never));
}
